import { FirstLetterPipe } from '@/_pipes';
import { NovatiqAuthorizationClient } from '@/_auth-client';
import { ROUTES } from '@/_constants';
import { AuthorizationHelper } from '@/_helpers';
import { ILanguage, LanguageService } from '@/_services/language.service';
import { IUserInfo } from '@/_types';
import { ShadowCustomerOrganisationDialogComponent } from '@/features/authorization/shadow-customer-organisation/shadow-customer-organisation-dialog.component';
import { UpperCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { Avatar } from 'primeng/avatar';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { DialogService } from 'primeng/dynamicdialog';
import { Menu } from 'primeng/menu';
import { Ripple } from 'primeng/ripple';
import { Select } from 'primeng/select';
import { Tooltip } from 'primeng/tooltip';
import { LayoutService } from './service/app.layout.service';
import { NovatiqMessageService } from './service/message.service';

@Component({
  selector: 'app-topbar',
  templateUrl: './app.topbar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    TranslatePipe,
    FormsModule,
    Menu,
    Select,
    Avatar,
    Ripple,
    ConfirmDialog,
    FirstLetterPipe,
    UpperCasePipe,
    Tooltip
  ]
})
export class AppTopBarComponent implements OnInit {
  private static readonly REVERT_SHADOW_CONFIRM_KEY = 'revertShadowConfirm';

  readonly layoutService = inject(LayoutService);
  private readonly authorizationClient = inject(NovatiqAuthorizationClient);
  private readonly authorizationHelper = inject(AuthorizationHelper);
  private readonly router = inject(Router);
  private readonly languageService = inject(LanguageService);
  private readonly messageService = inject(NovatiqMessageService);
  private readonly dialogService = inject(DialogService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  readonly locales = input<ILanguage[]>([]);
  readonly menuClicked = output<string>();

  @ViewChild('menubutton') menuButton!: ElementRef<HTMLElement>;
  @ViewChild('topbarmenubutton') topbarMenuButton!: ElementRef<HTMLElement>;
  @ViewChild('topbarmenu') menu!: ElementRef<HTMLElement>;

  readonly items = signal<MenuItem[]>([]);
  readonly currentUser = signal<IUserInfo | null>(null);
  readonly userName = signal('');
  readonly sessionLanguage = signal<string | null>(null);

  readonly languages = computed(() =>
    this.locales().map((language) => ({
      ...language,
      default: language.iso === this.authorizationClient.locale
    }))
  );

  readonly showShadowSessionIndicator = computed(
    () => this.authorizationClient.isShadowSession && this.currentUser() !== null
  );

  readonly shadowOrganisationName = computed(() => {
    const organisation = this.currentUser()?.organisation as
      | (IUserInfo['organisation'] & { friendly_name?: string })
      | undefined;
    return organisation?.friendlyName?.trim() || organisation?.friendly_name?.trim() || organisation?.name || '';
  });

  readonly shadowRoleLabel = computed(() => {
    const roleName = this.currentUser()?.roleName ?? '';
    if (!roleName) {
      return '';
    }
    const key = `roles.${roleName}`;
    const translated = this.translate.instant(key);
    return translated === key ? roleName : translated;
  });

  readonly shadowSessionStatusText = computed(() =>
    this.translate.instant('loggedInAsShadowSession', {
      organisation: this.shadowOrganisationName(),
      role: this.shadowRoleLabel()
    })
  );

  ngOnInit(): void {
    this.refreshMenuItems();
    this.setCurrentUser(this.authorizationClient.loggedInUser);

    this.authorizationClient
      .getCurrentUser()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user) => {
        this.refreshMenuItems();
        this.setCurrentUser(user);
      });

    this.languageService.sessionLanguage$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((language) => {
      this.sessionLanguage.set(language);
    });
  }

  private setCurrentUser(user: IUserInfo | null): void {
    this.currentUser.set(user);
    this.userName.set(user?.name || user?.userName || '');
  }

  isActive(route: string): boolean {
    return this.router.url === route;
  }

  onLanguageChange(): void {
    const language = this.sessionLanguage();
    if (!language) {
      return;
    }

    this.languageService.setSessionLanguage(language);
    this.languageService.useLanguage(language);
  }

  onMenuItemClick(event: Event, item: MenuItem): void {
    if (item.command) {
      event.preventDefault();
      item.command({ originalEvent: event, item });
    }
  }

  logoutCalled(): void {
    this.menuClicked.emit('logout');
  }

  showErrorMessage(message: string): void {
    this.messageService.sendMessage(message || '', 'error');
  }

  openShadowDialog(): void {
    this.dialogService.open(ShadowCustomerOrganisationDialogComponent, {
      header: this.translate.instant(
        this.authorizationClient.isShadowSession ? 'switchCustomerOrganisation' : 'logInAsCustomerOrganisation'
      ),
      width: '600px',
      breakpoints: {
        '991px': '95vw'
      },
      contentStyle: { overflow: 'auto', maxHeight: '85vh' },
      baseZIndex: 10000,
      dismissableMask: true,
      closable: true,
      modal: true
    });
  }

  confirmRevertShadow(): void {
    this.confirmationService.confirm({
      key: AppTopBarComponent.REVERT_SHADOW_CONFIRM_KEY,
      message: this.translate.instant('revertToNovatiqControlConfirm'),
      acceptLabel: this.translate.instant('proceed'),
      rejectLabel: this.translate.instant('cancel'),
      accept: () => {
        this.authorizationHelper.revertShadowSession().subscribe({
          error: (error) => {
            this.showErrorMessage(error?.message ?? this.translate.instant('shadowSessionError'));
          }
        });
      }
    });
  }

  private refreshMenuItems(): void {
    this.items.set(this.buildMenuItems());
    this.changeDetectorRef.markForCheck();
  }

  private buildMenuItems(): MenuItem[] {
    const items: MenuItem[] = [
      {
        label: 'Language',
        icon: 'pi pi-globe top-menu-icon',
        escape: true
      },
      {
        label: ROUTES.SETTINGS.label,
        icon: 'pi pi-cog top-menu-icon',
        routerLink: [ROUTES.SETTINGS.url],
        styleClass: this.isActive(ROUTES.SETTINGS.url) ? 'highlight' : ''
      },
      {
        label: ROUTES.SUPPORT.label,
        tooltip: ROUTES.SUPPORT.path,
        icon: 'pi support-icon top-menu-icon',
        routerLink: [ROUTES.SUPPORT.url],
        styleClass: this.isActive(ROUTES.SUPPORT.url) ? 'highlight' : ''
      }
    ];

    if (this.authorizationClient.isShadowSession) {
      items.push(
        {
          label: 'switchCustomerOrganisation',
          icon: 'pi pi-sync top-menu-icon',
          id: 'shadow-switch-menu-item',
          command: () => this.openShadowDialog()
        },
        {
          label: 'revertToNovatiqControl',
          icon: 'pi pi-replay top-menu-icon',
          id: 'shadow-revert-menu-item',
          command: () => this.confirmRevertShadow()
        }
      );
    } else if (this.authorizationClient.isNovatiqUser) {
      items.push({
        label: 'logInAsCustomerOrganisation',
        icon: 'pi pi-users top-menu-icon',
        id: 'shadow-login-menu-item',
        command: () => this.openShadowDialog()
      });
    }

    items.push({
      label: 'logout',
      icon: 'pi pi-sign-out top-menu-icon',
      command: () => {
        this.logoutCalled();
      }
    });

    return items;
  }
}
